import { useSeoApi } from '@/api/sdk-api';
import { enqueueSnackbar } from '@/components/SnackBar';
import useLocalStorage from '@/hooks/useLocalStorage';
import { isValidDateString } from '@/pages/report/utils/date-helpers';
import { useLazyGetMoscowDateTimeQuery } from '@/redux/api/other-api';
import { setDate } from '@/redux/slices/comparisons-data-slice';
import { setTipsMode } from '@/redux/slices/tip-slice';
import { setUser } from '@/redux/slices/user-slice';
import { type RootState } from '@/redux/store';
import { getAuthApiService } from '@/utils/agents/auth-api';
import { getSessionApiService } from '@/utils/agents/session-api';
import {
  getAuthReturnCabinetAccountId,
  redirectToCurrentAuthSigninUrl,
} from '@/utils/create-auth-signin-url';
import { getSessionManager } from '@/utils/session';
import { type CommonMailActions } from '@marpla/api-sdk';
import { useVisibilityChange } from '@marpla/core/hooks/use-visibility-change.hooks';
import MarplaHeader, { getMarplaExtensionNavLink } from '@marpla/ui/components/Header';
import { type BalanceProps } from '@marpla/ui/components/Header/Balance';
import IconButton from '@marpla/ui/components/IconButton';
import SettingsDialog from '@marpla/ui/components/SettingsDialog';
import SwitchLabel from '@marpla/ui/components/SwitchLabel';
import Typography from '@marpla/ui/components/Typography';
import LogOutIcon from '@marpla/ui/icons/LogOut';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, Stack } from '@mui/material';
import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import styles from './index.module.scss';
import navLinks from './navlinks';
import { IS_LOCALHOST } from '../../constants/Constants';

const authApi = getAuthApiService();
const sessionApi = getSessionApiService();

function UserMenuContent(): JSX.Element {
  const [isOpenSettings, setIsOpenSettings] = useState<boolean>(false);
  const { email: userEmail } = useSelector((state: RootState) => state.user);
  const { tipsMode } = useSelector((state: RootState) => state.tipsMode);
  const [isTipsMode, setIsTipsMode] = useLocalStorage<boolean>('isTipsMode', false, {
    serializable: true,
  });
  const dispatch = useDispatch();
  const [newEmail, setNewEmail] = useState<string>('');
  const selectedAccountId = useSelector((state: RootState) => state.user.accountId);

  const { resetToken, getUserData } = useSeoApi();
  const onLogOut = useCallback(async () => {
    if (window.confirm('Выйти из аккаунта?')) {
      const cabinetAccountId = getAuthReturnCabinetAccountId(selectedAccountId);

      try {
        await resetToken();
      } catch {
      } finally {
        if (!IS_LOCALHOST) {
          redirectToCurrentAuthSigninUrl(cabinetAccountId);
        }
      }
    }
  }, [resetToken, selectedAccountId]);

  const onCopyEmail = useCallback(() => {
    navigator.clipboard.writeText(String(userEmail)).then(
      () => {
        enqueueSnackbar('Email скопирован', {
          variant: 'success',
          autoHideDuration: 2000,
        });
      },
      () => {
        enqueueSnackbar('Копирование из браузера не поддерживается системой', {
          variant: 'error',
          autoHideDuration: 2000,
        });
      },
    );
  }, [userEmail]);

  const onSwitchedTipsMode = useCallback(() => {
    dispatch(setTipsMode(!tipsMode));
    setIsTipsMode((prev) => !prev);
  }, [dispatch, setIsTipsMode, tipsMode]);

  return (
    <Stack sx={{ p: '0 16px' }}>
      <Box>
        <SwitchLabel
          label="Подсказки в интерфейсе"
          checked={isTipsMode}
          onChange={onSwitchedTipsMode}
        />
      </Box>
      <div className={classnames(styles.navLink, styles.disableHover)}>
        <span onClick={onCopyEmail} className={styles.email}>
          {userEmail || 'Аккаунт'}
        </span>
        <IconButton variant="secondary" size="md" sx={{ ml: 1 }} onClick={onLogOut}>
          <LogOutIcon />
        </IconButton>
      </div>
      <SettingsDialog
        open={isOpenSettings}
        onClose={() => {
          setIsOpenSettings(false);
          setNewEmail('');
        }}
        mail={userEmail || 'E-mail'}
        onSubmitMail={async (mail) => {
          await authApi.setEmail({ email: mail });
          setNewEmail(mail);
        }}
        onOtpSubmit={async (otp, type) => {
          try {
            const { hash } = await authApi.getEmailHash({ email: newEmail, code: otp });

            await authApi.verifyEmailHash({
              hash,
              email: newEmail,
              action: (type === 'password' ? 'reset_password' : 'set_email') as CommonMailActions,
            });

            const sessionOut = await authApi.confirmEmail({ email: newEmail, hash });
            const access = await sessionApi.createAccess({
              refreshToken: String(sessionOut.refreshToken),
            });
            getSessionManager().applySessionAccess(access);

            const { email } = (await getUserData()) as { email: string };
            dispatch(setUser({ email }));

            enqueueSnackbar('Подтверждение почты успешно завершено', {
              variant: 'success',
              autoHideDuration: 3000,
            });

            setNewEmail('');
            setIsOpenSettings(false);
          } catch (error: any) {
            const serverMessage = error?.data?.message;
            const errorText =
              serverMessage === 'BAD_CODE' || serverMessage === 'INVALID_OR_BLOCKED_REFRESH_TOKEN'
                ? 'Неверный код или устаревшая сессия'
                : 'Ошибка подтверждения почты';

            enqueueSnackbar(errorText, {
              variant: 'error',
              autoHideDuration: 4000,
            });

            throw error;
          }
        }}
        onSubmitPassword={async (email) => {
          await authApi.resetPassword({ email });
        }}
      />
      <Typography
        onClick={() => setIsOpenSettings((prev) => !prev)}
        display={'flex'}
        gap={1}
        sx={{ cursor: 'pointer' }}
      >
        <SettingsOutlinedIcon />
        <Typography>Настройки</Typography>
      </Typography>
    </Stack>
  );
}

export default function Header(): JSX.Element {
  const dispatch = useDispatch();
  const [triggerGetMoscowDateTimeMutation] = useLazyGetMoscowDateTimeQuery();
  const user = useSelector((state: RootState) => state.user);
  const [moscowDate, setMoscowDate] = useState<Date | null>(null);

  const marplaExtensionNavLink = getMarplaExtensionNavLink(getSessionManager().getAccessToken());

  const navLinksWithMarplaExtension = useMemo(
    // TODO: https://jira.marpla.ru/browse/MP-913
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    () => [...navLinks.slice(0, 4), marplaExtensionNavLink, ...navLinks.slice(4)],
    [marplaExtensionNavLink],
  );

  const intervalRef = useRef<number | null>(null);

  const loadMoscowDate = useCallback(async () => {
    const res = await triggerGetMoscowDateTimeMutation({});
    const datetime = res.data?.datetime;

    if (datetime && isValidDateString(datetime)) {
      const serverDate = new Date(`${datetime.replace(' ', 'T')}+03:00`);

      dispatch(setDate(datetime.slice(0, 10)));

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      setMoscowDate(serverDate);

      intervalRef.current = window.setInterval(() => {
        setMoscowDate((prev) => (prev ? new Date(prev.getTime() + 1000) : prev));
      }, 1000);
    }
  }, [triggerGetMoscowDateTimeMutation, dispatch]);

  useVisibilityChange({
    onActivate: async () => {
      await loadMoscowDate();
    },
  });

  const getMoscowDate = useCallback(
    (): Promise<Date | null> => Promise.resolve(moscowDate ?? null),
    [moscowDate],
  );

  const balanceProps: BalanceProps = {
    bonusTime: user.bonusTime.hasTime ? user.bonusTime : undefined,
    linkTo: '/finance',
    balance: user.balance || 0,
  };

  useEffect(() => {
    void loadMoscowDate();
  }, [loadMoscowDate]);

  return (
    <MarplaHeader
      navLinks={navLinksWithMarplaExtension}
      userMenuContent={<UserMenuContent />}
      // @ts-ignore TODO: https://jira.marpla.ru/browse/MP-913
      getMoscowDate={getMoscowDate}
      balanceProps={user.isLoadingFinanceData ? undefined : balanceProps}
    />
  );
}
